import {AUTH_REDIRECT_URL_QUERY} from './constants';

export type AuthCallback = {
  /** The current URL with token and redirect params stripped. */
  cleanUrl: string;
  /** Always a same-origin path. Never an absolute foreign URL. */
  redirectUrl: string;
  token?: string;
};

/**
 * Reduce a caller-supplied redirect target to a same-origin path.
 *
 * The value arrives from the `redirect_url` query param, which anyone can set.
 * Legitimate callers always pass this app's own `window.location.href` (see
 * use-login-button.ts), so constraining it to our origin costs real users
 * nothing while closing the open redirect: without this, a crafted
 * `/auth?token=...&redirect_url=https://evil.example` sends the freshly
 * authenticated visitor off-site.
 *
 * Returns a path (never an absolute URL) so the caller cannot be talked into a
 * protocol-relative or cross-origin destination.
 */
const sameOriginPath = (value: string | null, origin: string): string => {
  if (!value) return '/';

  // The param is encoded by the caller and may survive one extra round of
  // encoding through the auth service; tolerate both shapes.
  let candidate = value;
  try {
    candidate = decodeURIComponent(value);
  } catch {
    candidate = value;
  }

  try {
    const destination = new URL(candidate, origin);
    if (destination.origin !== origin) return '/';

    return `${destination.pathname}${destination.search}${destination.hash}` || '/';
  } catch {
    return '/';
  }
};

/**
 * Parse the auth callback, and produce a URL safe to put in browser history.
 *
 * `cleanUrl` drops `token` and `redirect_url`. The caller replaces the history
 * entry with it before doing anything else, so the bearer token stops travelling
 * in `document.referrer`, in analytics/session-replay payloads that capture
 * `location.href`, and in the back-button history.
 */
export const readAuthCallback = (href: string, origin: string): AuthCallback => {
  let callbackUrl: URL;
  try {
    callbackUrl = new URL(href, origin);
  } catch {
    return {cleanUrl: '/', redirectUrl: '/'};
  }

  const token = callbackUrl.searchParams.get('token') || undefined;
  const redirectUrl = sameOriginPath(callbackUrl.searchParams.get(AUTH_REDIRECT_URL_QUERY), origin);

  callbackUrl.searchParams.delete('token');
  callbackUrl.searchParams.delete(AUTH_REDIRECT_URL_QUERY);
  const cleanUrl = `${callbackUrl.pathname}${callbackUrl.search}${callbackUrl.hash}`;

  return {cleanUrl: cleanUrl || '/', redirectUrl, token};
};
